import { createFileRoute } from "@tanstack/react-router";
import type { MaterialSrc } from "common/materials/materialSrc";
import { materialCacheKey } from "common/materials/materialSrc";
import { materialSrcFromPathString } from "common/materials/materialSrcFromPathString";
import { downloadBlob } from "common/utils/downloadFile";
import { linearToLogScale, logToLinearScale } from "common/utils/math";
import perfNow from "common/utils/perfNow";
import { EnvPreset } from "gfx/EnvPreset";
import { HTTPMaterialParameterLoader } from "gfx/HTTPMaterialParameterLoader";
import { forgetCachedHeightmaps } from "gfx/materials/baking/heightMapFromDesignMaterial";
import { designMaterialNew } from "gfx/materials/designMaterial";
import { parseDesignMaterialParams } from "gfx/materials/designMaterialParams";
import {
  BakeColorTextureMode,
  DetailRenderMode,
  defaultDesignMaterialLoadOptions,
} from "gfx/materials/designMaterialRuntimeConfig";
import type { DesignMaterialParams } from "gfx/materials/designMaterialTypes";
import { logMaterialSrc } from "gfx/materials/logMaterialSrc";
import { loadAllMaterialEditorTextures } from "gfx/materials/materialEditorTextures";
import PerspectiveControls from "gfx/PerspectiveControls";
import { envPresetSceneLights } from "gfx/sceneLights";
import { DoubleSidedPlaneGeometry } from "gfx/threejs/higharc/DoubleSidedPlaneGeometry";
import { MaterialSharedParameterGroup } from "gfx/threejs/higharc/MaterialSharedParameterGroup";
import THREEViewer from "gfx/THREEViewer";
import { getMaterialPathAutofillOptions } from "lib/components/studio/inspector/paletteMaterialUtils";
import { materialAppearanceTestDefaultMaterials } from "lib/house/materialLists";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import * as THREE from "three";
import Button from "ui/buttons/Button";
import Checkbox from "ui/forms/CheckboxOld";
import Select from "ui/forms/Select";
import withAuthentication from "../../components/withAuthentication";
import styles from "./styles/gfxDebugPage.module.scss";

const defaultCameraSettings = {
  name: "Default",
  fov: 20,
  ortho: false,
  x: 0,
  y: 0,
  z: 650,
  targetX: 0,
  targetY: 0,
  targetZ: 0,
  upX: 0,
  upY: 1,
  upZ: 0,
  cameraNear: 5,
  cameraFar: 2000,
};

const MESH_SIZE = 50;
const MESH_SPACING = MESH_SIZE * 1.35;
const MESH_SPACING_Y = MESH_SIZE * 1.05;

let planeGeo: THREE.BufferGeometry, closeUpPlaneGeo: THREE.BufferGeometry, cubeGeo: THREE.BufferGeometry;

function scaleGeoUvs(geo: THREE.BufferGeometry, scale: number) {
  const uv = (geo.getAttribute("uv") as THREE.BufferAttribute).array as Float32Array;
  for (let i = 0, l = uv.length; i < l; i++) {
    uv[i] *= scale;
  }
}

function scaleGeoUvsXY(geo: THREE.BufferGeometry, scaleX: number, scaleY: number) {
  const uv = (geo.getAttribute("uv") as THREE.BufferAttribute).array as Float32Array;
  for (let i = 0, l = uv.length / 2; i < l; i++) {
    uv[i * 2] *= scaleX;
    uv[i * 2 + 1] *= scaleY;
  }
}

function createGeoIfNeeded() {
  if (planeGeo !== undefined) {
    return;
  }
  planeGeo = new DoubleSidedPlaneGeometry(MESH_SIZE * 0.8, MESH_SIZE, 1, 1);
  planeGeo.userData.isCached = true;
  scaleGeoUvsXY(planeGeo, MESH_SIZE * 0.8, MESH_SIZE);

  closeUpPlaneGeo = new DoubleSidedPlaneGeometry(MESH_SIZE * 0.5, MESH_SIZE * 0.5, 1, 1);
  closeUpPlaneGeo.userData.isCached = true;
  scaleGeoUvs(closeUpPlaneGeo, MESH_SIZE * 0.1);

  cubeGeo = new THREE.BoxGeometry(MESH_SIZE * 0.3, MESH_SIZE * 0.3, MESH_SIZE * 0.3, 1, 1, 1);
  cubeGeo.userData.isCached = true;
  scaleGeoUvs(cubeGeo, MESH_SIZE * 0.3);
}

function openFiles(allowMultiple, onFilesCallback) {
  const d = document.createElement("input") as HTMLInputElement;
  d.type = "file";
  if (allowMultiple) {
    d.multiple = true;
  }
  d.accept = "application/json,.json";
  document.body.appendChild(d);
  d.onchange = () => {
    onFilesCallback(d.files);
  };
  d.click();
  document.body.removeChild(d);
}

function MaterialAppearanceTestPage() {
  const [materialSrcs, setMaterialSrcs] = useState<MaterialSrc[]>(materialAppearanceTestDefaultMaterials);
  const [materialParamsFromFiles, setMaterialParamsFromFiles] = useState<DesignMaterialParams[]>([]);
  const [forceDetailRenderMode, setForceDetailRenderMode] = useState<DetailRenderMode | undefined>(undefined);
  const [isolateModeSupported, setIsolateModeSupported] = useState<boolean>(true);
  const [parallaxEffectEnabled, setParallaxEffectEnabled] = useState<boolean>(true);
  const [useWebgl2TextureAtlas, setUseWebgl2TextureAtlas] = useState<boolean>(true);
  const [measureLoadPerformance, setMeasureLoadPerformance] = useState<boolean>(false);
  const [uvScaling, setUvScaling] = useState<number>(1.0);
  const [bakingMode, setBakingMode] = useState<BakeColorTextureMode>(BakeColorTextureMode.DONT_BAKE);

  const [lastPerformanceResult, setLastPerformanceResult] = useState<number>(0);

  useEffect(() => {
    void renderMaterials();
  }, [
    materialSrcs,
    materialParamsFromFiles,
    bakingMode,
    forceDetailRenderMode,
    isolateModeSupported,
    parallaxEffectEnabled,
    useWebgl2TextureAtlas,
    measureLoadPerformance,
    uvScaling,
  ]);

  const viewerContainerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<THREEViewer | undefined>(undefined);

  const fetchedTextures = useRef<
    | {
        [name: string]: THREE.Texture;
      }
    | undefined
  >(undefined);
  const displayMeshesRef = useRef<THREE.Mesh[] | undefined>(undefined);

  useEffect(() => {
    // Note that not loading the env map will result in the placeholder env map being used.
    const sceneLights = {
      ...envPresetSceneLights[EnvPreset.WHITEBOX],
      interiorAmbientLight: undefined, // disable the interior ambient light
    };

    const viewer = new THREEViewer({
      container: viewerContainerRef.current,
      options: {
        gui: false,
        ssfxRenderer: true,
        animate: true,
        sceneLights,
        debugFeaturesEnabled: true,
        cameraSettings: defaultCameraSettings,
      },
    });

    void (async () => {
      fetchedTextures.current = await loadAllMaterialEditorTextures();
      viewerRef.current = viewer;
      await renderMaterials();
    })();

    return () => {
      if (viewerRef.current) {
        const viewer = viewerRef.current;
        viewer.scene.clear();
        viewer.dispose();
        viewerRef.current = undefined;
      }
    };
  }, []);

  function tryImportFiles() {
    openFiles(true, (files) => {
      for (let i = 0; i < files.length; ++i) {
        const reader = new FileReader();
        reader.onloadend = (e) => {
          if (e.loaded) {
            const params = parseDesignMaterialParams(reader.result as string);

            if (params) {
              setMaterialParamsFromFiles([...materialParamsFromFiles, params]);
            }
          }
        };

        reader.readAsText(files[i]);
      }
    });
  }

  function removeMeshes() {
    const displayMeshes = displayMeshesRef.current;
    const viewer = viewerRef.current;
    if (displayMeshes === undefined || !viewer) {
      return;
    }
    for (let meshIndex = 0; meshIndex < displayMeshes.length; ++meshIndex) {
      const mesh = displayMeshes[meshIndex];
      viewer.scene.remove(mesh);
    }
    displayMeshesRef.current = [];
  }

  interface MaterialParamsAndSrc {
    params: DesignMaterialParams;
    materialSrc?: MaterialSrc;
  }

  async function renderMaterials() {
    if (!viewerRef.current || !fetchedTextures.current) {
      return;
    }

    const paramsList: MaterialParamsAndSrc[] = [];
    for (const materialSrc of materialSrcs) {
      const materialParams: DesignMaterialParams | null =
        await HTTPMaterialParameterLoader.instance.fetchDesignMaterialParams(materialSrc, true);
      if (materialParams) {
        paramsList.push({ params: materialParams, materialSrc });
      }
    }

    paramsList.push(...materialParamsFromFiles.map((params) => ({ params })));

    await renderMaterialParams(paramsList);
  }

  async function renderMaterialParams(paramsList: MaterialParamsAndSrc[]) {
    const viewer = viewerRef.current;
    const textures = fetchedTextures.current;

    const randomizeShaderSeed = new Date().getTime() & (2 ** 32 - 1);

    if (!viewer || !textures) {
      return;
    }

    const renderer = viewer.viewerRenderer.renderer;

    MaterialSharedParameterGroup.instance.sharedDefines.parallaxEffectEnabled = parallaxEffectEnabled;
    viewer.scene.ssfxRendererScene?.updateRenderParams({ renderPipelineParams: { isolateModeSupported } });

    if (measureLoadPerformance) {
      await forgetCachedHeightmaps(renderer);
    }

    for (const { params } of paramsList) {
      if (measureLoadPerformance) {
        params.randomizeShader = true;
        params.randomizeShaderSeed = randomizeShaderSeed;
      } else {
        params.randomizeShader = false;
      }
    }

    createGeoIfNeeded();

    const t0 = perfNow();

    // Put the material meshes in a 2D lattice.
    const materialsPerRow = Math.ceil(paramsList.length / 4);
    const materialRows = Math.ceil(paramsList.length / materialsPerRow);

    removeMeshes();

    if (displayMeshesRef.current === undefined) {
      displayMeshesRef.current = [];
    }
    const displayMeshes = displayMeshesRef.current;

    const setupMesh = (mesh: THREE.Mesh) => {
      mesh.updateMatrixWorld();
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      viewer.scene.add(mesh);
      displayMeshes.push(mesh);
    };

    let planeGeoToUse = planeGeo;
    let closeUpPlaneGeoToUse = closeUpPlaneGeo;
    let cubeGeoToUse = cubeGeo;
    if (uvScaling !== 1.0) {
      planeGeoToUse = planeGeo.clone();
      closeUpPlaneGeoToUse = closeUpPlaneGeo.clone();
      cubeGeoToUse = cubeGeo.clone();
      planeGeoToUse.userData.isCached = false;
      closeUpPlaneGeoToUse.userData.isCached = false;
      cubeGeoToUse.userData.isCached = false;
      scaleGeoUvs(planeGeoToUse, uvScaling);
      scaleGeoUvs(closeUpPlaneGeoToUse, uvScaling);
      scaleGeoUvs(cubeGeoToUse, uvScaling);
    }

    for (let paramsIndex = 0, paramsL = paramsList.length; paramsIndex < paramsL; ++paramsIndex) {
      const materialParams = paramsList[paramsIndex];
      const material = await designMaterialNew(
        materialParams.params,
        renderer,
        (url, _overrideFormat) => [textures[url], Promise.resolve()],
        viewer.hasSSFXRenderer,
        materialParams.materialSrc,
        {
          ...defaultDesignMaterialLoadOptions,
          bakeColorTexture: bakingMode,
          forceDetailRenderMode,
          useWebgl2TextureAtlas,
        },
      );

      const posX = ((paramsIndex % materialsPerRow) - materialsPerRow * 0.5 + 0.5) * MESH_SPACING;
      const posY = (Math.floor(paramsIndex / materialsPerRow) - materialRows * 0.5 + 0.5) * MESH_SPACING_Y;

      const planeMesh = new THREE.Mesh(planeGeoToUse, material);
      planeMesh.position.set(posX - MESH_SIZE * 0.25, posY, 0);
      setupMesh(planeMesh);

      const closeUpPlaneMesh = new THREE.Mesh(closeUpPlaneGeoToUse, material);
      closeUpPlaneMesh.position.set(posX + MESH_SIZE * 0.43, posY - MESH_SIZE * 0.25, 0);
      setupMesh(closeUpPlaneMesh);

      const cubeMesh = new THREE.Mesh(cubeGeoToUse, material);
      cubeMesh.rotation.set(0, Math.PI * 0.25, Math.PI * 0.25);
      cubeMesh.position.set(posX + MESH_SIZE * 0.42, posY + MESH_SIZE * 0.28, 0);
      cubeMesh.scale.set(1.05, 1.05, 1.05);
      setupMesh(cubeMesh);
    }

    if (measureLoadPerformance) {
      viewer.viewerRenderer?.forceRenderAndFinish();
      const t1 = perfNow();
      const measurement = t1 - t0;
      console.log("Last performance test result: " + measurement + " milliseconds");
      setLastPerformanceResult(measurement);
    }
  }

  return (
    <>
      <Head>
        <title>{getPageTitle("Material Load Test")}</title>
        <meta name="robots" content="noindex" />
      </Head>
      <div>
        <div className={`${styles.fullScreenViewerContainer}`} ref={viewerContainerRef} />
        <div className={styles.buttonContainerTopLeft}>
          <div className={styles.horizontalFlexRow}>
            ({materialParamsFromFiles.length} params currently loaded from files)
            <Button btnStyle="primary" btnSize="medium" onClick={tryImportFiles}>
              Load material params from files...
            </Button>
            <Button btnStyle="primary" btnSize="medium" onClick={() => setMaterialParamsFromFiles([])}>
              Reset params loaded from files
            </Button>
          </div>
          <Select
            value=""
            placeholder="Load material by ID"
            options={(input) => getMaterialPathAutofillOptions(input, true)}
            labelFromValue={(value) => value as string}
            onChange={(value) => {
              const materialSrc = materialSrcFromPathString(value as string);
              if (materialSrc) {
                setMaterialSrcs([...materialSrcs, materialSrc]);
              }
            }}
          />
          {materialSrcs.map((matSrc) => (
            <div key={materialCacheKey(matSrc)} className={styles.materialSrc}>
              <pre>{logMaterialSrc(matSrc, true)}</pre>
              <button onClick={() => setMaterialSrcs(materialSrcs.filter((src) => src !== matSrc))}>X</button>
            </div>
          ))}
        </div>
        <div className={styles.buttonContainerBottomLeft}>
          <div className={styles.horizontalFlexRow}>
            <Button
              btnStyle="primary"
              btnSize="medium"
              onClick={() => {
                const viewer = viewerRef.current;
                if (!viewer) {
                  return;
                }
                viewer.setCameraSettings(defaultCameraSettings);
                const cameraControls = new PerspectiveControls(viewer.camera, viewer.container, 0, 0, 0);
                viewer.setControls(cameraControls);
              }}
            >
              Reset Camera
            </Button>
            <Button
              btnStyle="primary"
              btnSize="medium"
              onClick={async () => {
                const viewer = viewerRef.current;
                if (!viewer || !viewer.viewerRenderer.ssfxParamsManager || !viewer.scene.ssfxRendererScene) {
                  return;
                }
                const renderWidth = 4096;
                const renderHeight = 2048;
                viewer.viewerRenderer.renderImageSync(
                  viewer.scene.ssfxRendererScene,
                  viewer.camera,
                  { width: renderWidth, height: renderHeight, ssaaSamples: 16 },
                  viewer.viewerRenderer.ssfxParamsManager.getRenderParams(),
                  (image) => {
                    image.toBlob((blob) => {
                      if (blob) {
                        downloadBlob("material-appearance-test.png", blob);
                      }
                    }, "image/png");
                  },
                );
              }}
            >
              Generate high-res PNG
            </Button>
          </div>
          <Select
            value={forceDetailRenderMode !== undefined ? DetailRenderMode[forceDetailRenderMode] : undefined}
            placeholder="Force Detail Render Mode"
            options={[
              { label: "None", value: "" },
              { label: "Parallax map", value: DetailRenderMode[DetailRenderMode.ParallaxMap] },
              { label: "SDF", value: DetailRenderMode[DetailRenderMode.SDF] },
            ]}
            onChange={(val) => {
              setForceDetailRenderMode(val !== undefined ? DetailRenderMode[val as string] : val);
            }}
          />
          <div className={styles.horizontalFlexRow}>
            <label htmlFor="uvScaling">UV Scaling:</label>
            <input
              type="range"
              id="uvScaling"
              min="0"
              max="1"
              step="0.02"
              defaultValue={logToLinearScale(uvScaling, 0.1, 10)}
              onChange={(e) => {
                const value = parseFloat(e.target.value);
                setUvScaling(Math.round(linearToLogScale(value, 0.1, 10) * 100) / 100);
              }}
              style={{ width: "200px" }}
            />
            <span id="uvScalingValue">{uvScaling}</span>
          </div>
          <Checkbox
            label="Use WebGL 2 texture atlas"
            checked={useWebgl2TextureAtlas}
            onChange={(e) => setUseWebgl2TextureAtlas(e.target.checked)}
          />
          <Select
            value={bakingMode}
            placeholder="Baking mode"
            options={[
              { label: "No baking", value: BakeColorTextureMode.DONT_BAKE },
              { label: "Repeating layers", value: BakeColorTextureMode.REPEATING_LAYERS },
              { label: "Bake on client", value: BakeColorTextureMode.BAKE },
              { label: "Fetch baked texture", value: BakeColorTextureMode.FETCH_FROM_AWS },
              { label: "Flat color", value: BakeColorTextureMode.FLAT_COLOR },
            ]}
            onChange={(val) => {
              setBakingMode(val !== undefined ? (val as BakeColorTextureMode) : val);
            }}
          />
          <Checkbox
            label="Isolate mode support"
            checked={isolateModeSupported}
            onChange={(e) => setIsolateModeSupported(e.target.checked)}
          />
          <Checkbox
            label="Parallax effect"
            checked={parallaxEffectEnabled}
            onChange={(e) => setParallaxEffectEnabled(e.target.checked)}
          />
          <Checkbox
            label={`Measure load performance ${
              bakingMode === BakeColorTextureMode.BAKE ? "(not fully supported while baking is enabled)" : ""
            }`}
            checked={measureLoadPerformance}
            onChange={(e) => setMeasureLoadPerformance(e.target.checked)}
          />
          <div>Latest load time measurement: {lastPerformanceResult}</div>
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute("/debug/material-appearance-test")({
  component: withAuthentication(MaterialAppearanceTestPage, { superAdmin: true }),
});
